import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  Bullhorn,
  Command,
  Edit2,
  PlusCircle,
  Sidebar,
  SparkleRefresh,
} from '@lifeomic/chromicons';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { ContainerQuery } from 'react-container-query';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useActiveAccountId, useFeatures } from '../../../../hooks';
import { useMixpanel } from '../../../../hooks/useMixpanel';
import { maskedModeActions } from '../../../../redux/modules/maskedMode';
import {
  ImpressionTypeFilter,
  KnownExtraSections,
} from '../../../AITemplates/ImpressionTypeFilter';
import EnforceABAC, { permissions } from '../../../EnforceABAC';
import { dashboardStateActions } from '../../../LayoutConfiguration/hooks/useLayoutConfigurationState';
import {
  CachedDashboardsContext,
  LayoutStateDispatchContext,
} from '../../../LayoutConfiguration/util/DashboardsContextRoot';
import { ActionPlanActivity } from '../ActionPlan/action-plan';
import { ViewMode } from '../AITools/Attachments/InlineAttachment';
import { KnownImpressionTypes } from '../AITools/ImpressionEditor/ImpressionItem';
import { ViewModePicker } from '../AITools/ViewModePicker';
import {
  useExecutiveSummaryData,
  useExecutiveSummarySelection,
  useExecutiveSummaryUI,
  useGenerateDrawer,
} from './Context';
import { RegenerateActionPlanModal } from '../ActionPlan/RegenerateActionPlanModal';
import { GenerateMenuModal } from './GenerateMenuModal';
import { keyboardShortcuts, messages } from './messages';
import { useExecutiveSummaryGeneration } from './useExecutiveSummaryGeneration';

const query = {
  isNarrow: {
    maxWidth: 740,
  },
};

const useStyles = makeStyles(theme => ({
  header: {
    backgroundColor: theme.palette.background.paper,
    borderCollapse: 'collapse',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    justifyContent: 'space-between',
    maxHeight: theme.pxToRem(60),
    '& button': {
      paddingLeft: theme.pxToRem(14),
      paddingRight: theme.pxToRem(14),
    },
  },
  toggleSidebarButton: {
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(2),
    marginLeft: theme.spacing(-1),
    '&:focus, &:focus-visible': {
      borderRadius: 0,
    },
  },
  editButton: {
    '&:disabled, &[disabled]': {
      backgroundColor: theme.palette.primary.main,
      borderRadius: theme.pxToRem(18),
      color: theme.palette.common.white,
      cursor: 'not-allowed',
      opacity: 1,
      minHeight: theme.pxToRem(35),
      paddingTop: theme.pxToRem(6),
      paddingBottom: theme.pxToRem(6),
    },
  },
  editIconButton: {
    '&:disabled, &[disabled]': {
      paddingTop: theme.pxToRem(12),
      paddingBottom: theme.pxToRem(12),
    },
  },
  regenerateAlert: {
    margin: theme.spacing(0, 1, 1),
    padding: theme.spacing(1, 1.25),
    width: `calc(100% - ${theme.spacing(2)})`,
  },
  alertIcon: {
    marginRight: theme.spacing(0.75),
    maxWidth: theme.pxToRem(18),
    maxHeight: theme.pxToRem(18),
    minWidth: theme.pxToRem(18),
    minHeight: theme.pxToRem(18),
  },
  viewModeMenu: {
    minWidth: 'unset',
    maxWidth: theme.pxToRem(200),
  },
  regenerateMenu: {
    maxWidth: theme.pxToRem(255),
  },
}));

export const ControlBar = (props: {
  patientId: string;
  disableControls: boolean;
  enabledSummaries?: string[];
  fixedImpressionType?: KnownImpressionTypes | KnownExtraSections;
}) => {
  const { disableControls, patientId, fixedImpressionType } = props;

  const { isGenerating } = useExecutiveSummaryGeneration({
    patientId,
  });
  const [isGenerateMenuModalOpen, setIsGenerateMenuModalOpen] = useState(false);
  // Split context usage for better performance
  const { executiveSummary, enabledImpressionTypes, isSaving } =
    useExecutiveSummaryData();

  const {
    impressionTypeFilter: _impressionTypeFilter,
    activeImpression,
    setActiveImpression,
    viewMode,
    setViewMode,
    setImpressionTypeFilter,
    setActiveActionItem,
    setSelectedActionItem,
  } = useExecutiveSummarySelection();

  const {
    isSidebarVisible,
    setSidebarVisible,
    isEditing,
    setIsEditing,
    setShowFeedbackModal,
    setIsPresentationModalOpen,
    setIsActionItemModalOpen,
  } = useExecutiveSummaryUI();

  const { setIsDrawerOpen, isSuggesting } = useGenerateDrawer();
  const isBlocked = isGenerating || isSuggesting;
  const blockedMessage = isSuggesting
    ? messages.suggestInProgress
    : messages.regenerateInProgress;

  const impressionTypeFilter = fixedImpressionType ?? _impressionTypeFilter;

  const dispatch = React.useContext(LayoutStateDispatchContext);
  const dashboards = React.useContext(CachedDashboardsContext);
  const hasFeature = useFeatures();
  // The drawer is never mounted when ControlBar is locked to actionItems mode
  // (i.e. the dedicated Action Plan page). Fall back to the modal so Regenerate
  // actually fires a network request.
  const shouldUseDrawer = fixedImpressionType !== 'actionItems';
  const activeAccountId = useActiveAccountId();
  const history = useHistory();
  const location = useLocation();
  const fountainLifeUXEnabled =
    hasFeature('fountainLifeUX') ||
    !!activeAccountId?.startsWith('fountainlife');
  const isMemberRoute = location.pathname.startsWith('/project/members/');

  const actionPlanDashboard = dashboards?.find(
    dashboard => dashboard.name.toLowerCase().indexOf('action plan') !== -1,
  );

  const handleImpressionFilterChange = (
    value: KnownImpressionTypes | KnownExtraSections,
  ) => {
    if (value === 'actionItems') {
      if (fountainLifeUXEnabled && isMemberRoute) {
        history.push(`/project/members/${patientId}/action-plan`);
        return;
      }
      actionPlanDashboard &&
        dispatch(
          dashboardStateActions.switchToLayout(actionPlanDashboard, true),
        );
      return;
    }
    if (fixedImpressionType) {
      setImpressionTypeFilter(fixedImpressionType);
      return;
    }
    setImpressionTypeFilter(value);
    const newFilteredImpressions = executiveSummary?.impressions.filter(
      impression =>
        enabledImpressionTypes.includes(impression.impressionType) &&
        impression.impressionType === value,
    );
    setActiveImpression(newFilteredImpressions?.[0]);
  };

  const { formatMessage } = useIntl();
  const mixpanel = useMixpanel();
  const classes = useStyles();
  const traceId = executiveSummary?.traceId;

  const reduxDispatch = useDispatch();
  const openPresentationView = useCallback(
    (mode: ViewMode) => {
      mixpanel.track(`annual_report_${mode}_view_opened`, {
        impressionType: activeImpression?.impressionType,
        impressionId: activeImpression?.impression.id,
      });
      setViewMode(mode);
      reduxDispatch(maskedModeActions.setMaskedMode(false));
      setIsPresentationModalOpen(true);
    },
    [
      reduxDispatch,
      activeImpression,
      mixpanel,
      setViewMode,
      setIsPresentationModalOpen,
    ],
  );

  if (!executiveSummary) {
    return null;
  }

  const isActionPlanContext = impressionTypeFilter === 'actionItems';
  const generateMenuModal = isActionPlanContext ? (
    <RegenerateActionPlanModal
      patientId={patientId}
      crdDurableId={executiveSummary?.compositeReportDefinitionDurableId}
      isOpen={isGenerateMenuModalOpen}
      onDismiss={() => setIsGenerateMenuModalOpen(false)}
    />
  ) : (
    <GenerateMenuModal
      patientId={patientId}
      executiveSummary={executiveSummary}
      isOpen={isGenerateMenuModalOpen}
      onDismiss={() => setIsGenerateMenuModalOpen(false)}
      isActionItemsRegenerate={false}
    />
  );

  return (
    <ContainerQuery query={query}>
      {params => (
        <Box className={classes.header} fullWidth>
          <Box>
            {!disableControls && (
              <Tooltip title={formatMessage(messages.toggleSidebar)}>
                <IconButton
                  className={classes.toggleSidebarButton}
                  aria-label={formatMessage(messages.toggleSidebar)}
                  onClick={() => setSidebarVisible(!isSidebarVisible)}
                  color="primary"
                  icon={Sidebar}
                  size={0}
                />
              </Tooltip>
            )}
            {!fixedImpressionType && (
              <ImpressionTypeFilter
                disabled={isEditing || isSaving} // disabled when editing to simplify and prevent data loss
                impressionTypes={enabledImpressionTypes}
                value={impressionTypeFilter}
                onChange={handleImpressionFilterChange}
              />
            )}
            {!params['isNarrow'] ? (
              <>
                {!disableControls &&
                  executiveSummary.status !== 'completed' &&
                  impressionTypeFilter !== 'actionItems' &&
                  impressionTypeFilter !== 'labResults' && (
                    <EnforceABAC key="edit" permission={permissions.updateData}>
                      <Button
                        className={classes.editButton}
                        key="edit"
                        onClick={() => setIsEditing(!isEditing)}
                        variant="text"
                        icon={Edit2}
                        disabled={
                          impressionTypeFilter === 'synopsis' || isEditing
                        }
                      >
                        {formatMessage(messages.editSummary)}
                      </Button>
                    </EnforceABAC>
                  )}
                {impressionTypeFilter === 'actionItems' && (
                  <Button
                    icon={PlusCircle}
                    variant="text"
                    onClick={() => {
                      setActiveActionItem(new ActionPlanActivity());
                      setSelectedActionItem(undefined);
                      setIsActionItemModalOpen(true);
                      mixpanel.track('annual_report_action_item_add_clicked');
                    }}
                  >
                    {formatMessage(messages.addActionItem)}
                  </Button>
                )}

                <Button
                  variant="text"
                  icon={SparkleRefresh}
                  disabled={isBlocked}
                  onClick={() =>
                    shouldUseDrawer
                      ? setIsDrawerOpen(prev => !prev)
                      : setIsGenerateMenuModalOpen(true)
                  }
                >
                  {executiveSummary.status === 'completed'
                    ? isBlocked
                      ? formatMessage(blockedMessage)
                      : formatMessage(messages.generate)
                    : isBlocked
                    ? formatMessage(blockedMessage)
                    : formatMessage(messages.regenerate)}
                </Button>

                {traceId && (
                  <Button
                    variant="text"
                    icon={Bullhorn}
                    onClick={() => setShowFeedbackModal(true)}
                  >
                    {formatMessage(messages.feedback)}
                  </Button>
                )}
                <Menu
                  aria-label={formatMessage(messages.keyboardShortcuts)}
                  anchorElement={
                    <Button variant="text" icon={Command}></Button>
                  }
                  items={[
                    <MenuItem key="keyboardShortcutsDescription">
                      <Box direction="column">
                        <Text size="caption" weight="bold">
                          {formatMessage(messages.keyboardShortcuts)}
                        </Text>
                        <Text size="caption">
                          {Object.keys(keyboardShortcuts).map(key => (
                            <Text key={key} size="caption">
                              {formatMessage(
                                keyboardShortcuts[
                                  key as keyof typeof keyboardShortcuts
                                ],
                              )}
                            </Text>
                          ))}
                        </Text>
                      </Box>
                    </MenuItem>,
                  ]}
                />
              </>
            ) : (
              <>
                {impressionTypeFilter !== 'actionItems' &&
                  impressionTypeFilter !== 'labResults' && (
                    <EnforceABAC key="edit" permission={permissions.updateData}>
                      <Tooltip title={formatMessage(messages.editSummary)}>
                        <IconButton
                          aria-label={formatMessage(messages.editSummary)}
                          className={clsx(
                            classes.editButton,
                            classes.editIconButton,
                          )}
                          key="edit"
                          onClick={() => setIsEditing(!isEditing)}
                          color="primary"
                          icon={Edit2}
                          size={0}
                          disabled={
                            impressionTypeFilter === 'synopsis' || isEditing
                          } // disable editing for synopsis
                        />
                      </Tooltip>
                    </EnforceABAC>
                  )}
                {impressionTypeFilter === 'actionItems' && (
                  <Tooltip title={formatMessage(messages.addActionItem)}>
                    <IconButton
                      aria-label={formatMessage(messages.addActionItem)}
                      onClick={() => {
                        setActiveActionItem(new ActionPlanActivity());
                        setSelectedActionItem(undefined);
                        setIsActionItemModalOpen(true);
                        mixpanel.track('annual_report_action_item_add_clicked');
                      }}
                      color="primary"
                      icon={PlusCircle}
                      size={0}
                    />
                  </Tooltip>
                )}
                <Tooltip
                  title={
                    isBlocked
                      ? formatMessage(blockedMessage)
                      : formatMessage(messages.regenerate)
                  }
                >
                  {/* Box wrapper required for tooltip to work in blocked state */}
                  <Box>
                    <IconButton
                      aria-label={formatMessage(messages.regenerate)}
                      onClick={() =>
                        shouldUseDrawer
                          ? setIsDrawerOpen(prev => !prev)
                          : setIsGenerateMenuModalOpen(true)
                      }
                      disabled={isBlocked}
                      color="primary"
                      icon={SparkleRefresh}
                      size={0}
                    >
                      {isBlocked
                        ? formatMessage(blockedMessage)
                        : formatMessage(messages.regenerate)}
                    </IconButton>
                  </Box>
                </Tooltip>
                {traceId && (
                  <Tooltip title={formatMessage(messages.feedback)}>
                    <IconButton
                      aria-label={formatMessage(messages.feedback)}
                      onClick={() => setShowFeedbackModal(true)}
                      color="primary"
                      icon={Bullhorn}
                      size={0}
                    />
                  </Tooltip>
                )}
              </>
            )}
          </Box>
          <Box>
            {!disableControls && (
              <ViewModePicker
                mode={viewMode}
                onSelect={newMode => {
                  setViewMode(newMode);
                  if (
                    newMode === 'presentation-expanded' ||
                    newMode === 'presentation-compact' ||
                    newMode === 'print'
                  ) {
                    openPresentationView(newMode);
                  }
                }}
              />
            )}
          </Box>
          {!shouldUseDrawer && generateMenuModal}
        </Box>
      )}
    </ContainerQuery>
  );
};
